import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  OnInit,
  SimpleChanges,
  signal,
  computed,
  ViewChild,
  HostListener,
  effect,
  inject,
  DestroyRef
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { toSignal, toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, timer, EMPTY } from 'rxjs';
import { switchMap, scan } from 'rxjs/operators';
import { TranscriptMetadataService } from '../../../../services/transcript-metadata/transcript-metadata.service';
import { TranscriptRealtimeService } from '../../../../services/transcript-realtime/transcript-realtime.service';
import { ToastService } from '../../../../services/toast-messages/toast-messages.service';
import { TranslatePipe } from '@ngx-translate/core';
import { FronteggAuthService } from '@frontegg/angular';
import {
  TranscriptExtractionData,
  Course,
  FieldWithBbox,
  RequiredFields
} from '../../../../models/transcriptExtractionData.model';
import { TranscriptMetadata, mapToTranscriptMetadata } from '../../../../models/documentResponse';
import { DocumentViewerComponent } from '../../../../components/document-viewer/document-viewer.component';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import {
  CollegeInstitutionRows,
  InstitutionMap,
  StatusConfig,
  StudentFieldRows,
  TranscriptRequiredFields,
  TranscriptStatus,
  TranscriptStatusLabels,
  TranscriptType,
  ValidateElementType,
  TranscriptTypeLabels,
  REVIEW_CONFIDENCE_THRESHOLD
} from '../../../../constants';
import { Popover } from 'primeng/popover';
import { Table, TableModule } from 'primeng/table';
import { TranscriptExportService } from '../../../../services/transcript-export/transcript-export.service';
import { TranscriptToExport } from '../../../../models/etrive.model';
import { ItpExtractionDataService } from '../../../../services/itp-extraction/itp-extraction-data.service';
import { ItpExtractionValidationService } from '../../../../services/itp-extraction/itp-extraction-validation.service';
import {
  BatchSisResponse,
  SisErrorCodes
} from 'projects/capture-app/src/app/models/sis-response.model';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { NgClass, DecimalPipe } from '@angular/common';
import { Divider } from 'primeng/divider';
import { Tooltip } from 'primeng/tooltip';
import { Panel } from 'primeng/panel';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { Menu } from 'primeng/menu';
import { ItpExtractionConfidenceComponent } from '../itp-extraction-confidence/itp-extraction-confidence.component';
import { getProcessorRoleInfo } from '../../../../utils/itp-processor-role.util';

@Component({
  selector: 'app-itp-extraction-data',
  templateUrl: './itp-extraction-data.component.html',
  styleUrls: ['./itp-extraction-data.component.scss'],
  imports: [
    Bind,
    Button,
    NgClass,
    Divider,
    Tooltip,
    Panel,
    PrimeTemplate,
    FormsModule,
    InputText,
    Popover,
    ToggleSwitch,
    TableModule,
    Checkbox,
    Dialog,
    ItpExtractionConfidenceComponent,
    DecimalPipe,
    TranslatePipe,
    SelectModule,
    Menu
  ]
})
export class ItpExtractionDataComponent implements OnDestroy, OnChanges, OnInit {
  private static readonly RANDOM_FACTS_URL = 'assets/data/random-facts.json';
  private static readonly FACT_ROTATION_INTERVAL_MS = 8000;

  router = inject(Router);
  private http = inject(HttpClient);
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private transcriptRealtimeService = inject(TranscriptRealtimeService);
  private toastService = inject(ToastService);
  private fronteggAuthService = inject(FronteggAuthService);
  private featureFlagService = inject(FeatureFlagService);
  private transcriptExportService = inject(TranscriptExportService);
  private itpExtractionDataService = inject(ItpExtractionDataService);
  private itpExtractionValidationService = inject(ItpExtractionValidationService);
  private destroyRef = inject(DestroyRef);

  @Input() documentId!: string;
  @Input() metadata?: TranscriptMetadata;
  @Input() documentViewer?: DocumentViewerComponent;
  @Output() lockStateChanged = new EventEmitter<{
    transcriptId: string;
    lockedByUser: string | null;
  }>();
  @Output() statusChanged = new EventEmitter<string>();

  isExtracting = signal(false);
  private randomFacts = signal<string[]>([]);
  randomFact = toSignal(
    toObservable(this.isExtracting).pipe(
      switchMap(isExtracting =>
        isExtracting
          ? timer(0, ItpExtractionDataComponent.FACT_ROTATION_INTERVAL_MS).pipe(
              scan(previousFact => this.pickRandomFact(this.randomFacts(), previousFact), '')
            )
          : EMPTY
      )
    ),
    { initialValue: '' }
  );

  @ViewChild('columnMenu') columnMenu!: Popover;
  @ViewChild('courseTable') courseTable!: Table;

  /**
   * Returns the display label for a given transcript type
   * @param typeId The transcript type to get the label for
   */
  getTypeLabel(typeId: TranscriptType): string {
    return TranscriptTypeLabels[typeId] ?? '';
  }

  showChangeTypeConfirmation = signal(false);
  newTranscriptType = signal<TranscriptType | null>(null);

  private autosaveTimeout: ReturnType<typeof setTimeout> | undefined;
  private requiredFieldsVersion = signal(0);
  private requiredFields: RequiredFields = TranscriptRequiredFields;

  selectedField = signal<FieldWithBbox | null>(null);
  private readonly statusEffect = effect(() => {
    const statusId = this.transcriptStatusId();
    if (statusId !== null) {
      this.statusChanged.emit(this.getStatusText(statusId));
    }
  });
  private readonly extractionInProgressEffect = effect(() => {
    const statusId = this.transcriptStatusId();
    const inProgress =
      statusId === TranscriptStatus.PendingExtraction || statusId === TranscriptStatus.Extracting;
    this.handleExtractionInProgressChanged(inProgress);
  });
  transcriptId: string = '';
  currentDocument = signal<TranscriptMetadata | null>(null);

  transcriptData: TranscriptExtractionData | null = null;
  dataLoadError = signal(false);
  dataLoadErrorReason = signal<'typeChange' | 'unknown'>('unknown');
  selectedCourse: Course | null = null;
  selectedCourses: Course[] = [];
  showDeleteCourseRowsConfirmation = signal(false);
  hasUnsavedChanges = signal(false);
  hasBeenEdited = signal(false);
  isSaving = signal(false);
  saveFailed = signal(false);
  showCompletionConfirmation = signal(false);
  shouldUnlockOnDestroy = true;
  selectedInputValue = signal<string>('');

  showMissingFieldsDialog = signal(false);
  missingFieldsSections = signal<string[]>([]);
  showValidationErrors = signal(false);

  confidenceModalVisible = false;
  selectedConfidenceField: FieldWithBbox | null = null;
  selectedConfidenceExplanation: string | null = null;
  fieldsNeedingAttentionCount = computed(() => {
    this.requiredFieldsVersion();
    return this.itpExtractionValidationService.calculateFieldsNeedingAttention(
      this.transcriptData,
      this.requiredFields
    );
  });

  isLocked = computed(() => !!this.currentDocument()?.lockedByUser);
  lockedByUser = computed(() => this.currentDocument()?.lockedByUser || null);
  isLockedByCurrentUser = computed(() => {
    if (!this.isLocked()) return false;
    const currentUser = this.fronteggAuthService.user;
    const currentUserDisplayName =
      (currentUser as any)?.customClaims?.display_name || currentUser?.name || '';
    return this.lockedByUser() === currentUserDisplayName;
  });
  isEditable = computed(
    () =>
      this.isLockedByCurrentUser() &&
      this.transcriptStatusId() !== TranscriptStatus.Archived &&
      !this.isExporting()
  );
  isReviewCompleted = computed(() => this.transcriptStatusId() === TranscriptStatus.ReadyForExport);

  hasItpAdminRole = computed(() => {
    const currentUser = this.fronteggAuthService.user;
    return !!currentUser?.roles?.some((role: any) => role.key === 'ITP Admin');
  });

  isRequiredFieldsFilled = computed(() => {
    this.requiredFieldsVersion();

    if (!this.transcriptData?.Courses || this.transcriptData.Courses.length === 0) {
      return false;
    }

    return this.itpExtractionValidationService.validateRequiredFields(
      this.transcriptData,
      this.requiredFields,
      this.transcriptType()
    ).isValid;
  });

  readonly showCourses = computed(() => {
    if (this.currentDocument()?.typeId == 1) {
      return false;
    }
    return true;
  });

  readonly studentFieldRows = StudentFieldRows;
  readonly transcriptType = computed(() => this.currentDocument()?.typeId ?? (0 as TranscriptType));

  readonly institutionFieldRows = computed(
    () => InstitutionMap.get(this.transcriptType()) ?? CollegeInstitutionRows
  );

  courseColumns = signal([
    { field: 'Term', label: 'Term', visible: true, sortable: true },
    { field: 'StartDate', label: 'Start Date', visible: false, sortable: true },
    { field: 'EndDate', label: 'End Date', visible: false, sortable: true },
    { field: 'Subject', label: 'Subject', visible: true, sortable: true },
    { field: 'CourseNumber', label: 'Number', visible: true, sortable: true },
    { field: 'CourseTitle', label: 'Title', visible: true, sortable: true },
    { field: 'CreditsEarned', label: 'Credits', visible: true, sortable: true },
    { field: 'Grade', label: 'Grade', visible: true, sortable: true },
    { field: 'GradePoints', label: 'Points', visible: true, sortable: true },
    { field: 'Institution', label: 'Institution', visible: true, sortable: true },
    { field: 'InstitutionId', label: 'Institution ID', visible: false, sortable: true }
  ]);

  columnMenuItems = computed<MenuItem[]>(() =>
    this.courseColumns().map(col => ({
      icon: col.visible ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off',
      label: col.label,
      command: event => {
        event.originalEvent?.stopPropagation();
        this.toggleColumn(col.field);
      }
    }))
  );

  visibleCourseColumns = computed(() => this.courseColumns().filter(col => col.visible));

  transcriptTypeMenuItems = computed<MenuItem[]>(() =>
    Object.values(TranscriptType)
      .filter((value): value is TranscriptType => typeof value === 'number')
      .map(type => ({
        label: this.getTypeLabel(type),
        disabled: type === this.transcriptType(),
        styleClass: `dap-change-type-option-${TranscriptType[type].toLowerCase()}`,
        command: () => this.onSelectTranscriptType(type)
      }))
  );

  private readonly statusConfig = StatusConfig;

  private readonly defaultStatus = {
    text: 'Processing',
    translationKey: 'transcriptData.defaultStatus',
    cssClass: 'status-default'
  };

  showSendToSISConfirmation = signal(false);
  isExporting = signal(false);
  enableSendToSis = toSignal(this.featureFlagService.getFlag('feature_itp_sendtosis', false));
  transcriptStatusId = signal<number | null>(null);
  readonly TranscriptStatus = TranscriptStatus;

  reviewIndicatorFlag = toSignal(
    this.featureFlagService.getFlag('feature-itp-review-indicators', false)
  );

  itpTypeFilterFlag = toSignal(
    this.featureFlagService.getFlag('feature-itp-high-school-transcripts', false)
  );

  isItpPowerProcessor = computed(
    () => getProcessorRoleInfo(this.fronteggAuthService.user?.roles).isPowerProcessor
  );

  /**
   * Handles document click to deselect selected course
   * @param event - The click event
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('p-table')) {
      this.selectedCourse = null;
    }
  }

  /**
   * Subscribes to live status and lock/unlock updates for this transcript.
   */
  async ngOnInit(): Promise<void> {
    this.http
      .get<string[]>(ItpExtractionDataComponent.RANDOM_FACTS_URL)
      .subscribe(facts => this.randomFacts.set(facts));

    await this.transcriptRealtimeService.connect();

    this.transcriptRealtimeService
      .onTranscriptStatusUpdate()
      .pipe(
        filter(update => update.transcriptId === this.transcriptId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(update => this.handleRealtimeStatusUpdate(update.status));

    this.transcriptRealtimeService
      .onTranscriptAssigned()
      .pipe(
        filter(update => update.transcriptId === this.transcriptId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(update => this.handleRealtimeLockChange(update.assignedTo));

    this.transcriptRealtimeService
      .onTranscriptUnassigned()
      .pipe(
        filter(update => update.transcriptId === this.transcriptId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.handleRealtimeLockChange(null));
  }

  /**
   * Applies a live transcript status push, mapping the status label back to
   * its numeric TranscriptStatus and updating local state.
   * @param statusLabel The transcript status label from the realtime update
   */
  private handleRealtimeStatusUpdate(statusLabel: string): void {
    const entry = Object.entries(TranscriptStatusLabels).find(([, label]) => label === statusLabel);
    if (!entry) return;
    this.transcriptStatusId.set(Number(entry[0]) as TranscriptStatus);
  }

  /**
   * Applies a live lock/unlock push for this transcript, keeping local state
   * and the parent's document list in sync.
   * @param lockedByUser The user who now holds the lock, or null if unlocked
   */
  private handleRealtimeLockChange(lockedByUser: string | null): void {
    this.currentDocument.update(doc => (doc ? { ...doc, lockedByUser } : doc));
    this.lockStateChanged.emit({ transcriptId: this.transcriptId, lockedByUser });
  }

  /**
   * Toggles the re-extraction loading screen and, once extraction finishes,
   * refreshes the transcript's metadata so the form reflects the latest data.
   * @param inProgress Whether the transcript is currently being extracted
   */
  private handleExtractionInProgressChanged(inProgress: boolean): void {
    if (!inProgress && this.isExtracting()) {
      this.refreshAfterReExtraction();
    }
    this.isExtracting.set(inProgress);
  }

  /**
   * Re-fetches transcript metadata after re-extraction completes and reloads
   * the extraction form.
   */
  private async refreshAfterReExtraction(): Promise<void> {
    try {
      const doc = await this.transcriptMetadataService.getTranscriptMetadata(this.transcriptId);
      this.metadata = mapToTranscriptMetadata(doc);
      this.currentDocument.set(this.metadata);
      await this.loadData();
    } catch (error) {
      console.error('Failed to refresh transcript metadata after re-extraction:', error);
      this.setDataLoadError(error);
    }
  }

  /**
   * Marks the data load as failed and records why, so the template can show
   * a specific message when the user has lost access to the transcript
   * (e.g. it was reclassified to a type they're not permitted to view).
   * @param error The error thrown while loading transcript data or metadata
   */
  private setDataLoadError(error: unknown): void {
    const isForbidden = error instanceof Error && error.message.includes('status: 403');
    this.dataLoadErrorReason.set(isForbidden ? 'typeChange' : 'unknown');
    this.dataLoadError.set(true);
  }

  /**
   * Picks a random fact distinct from the previously shown one, when possible.
   * @param facts The pool of facts to pick from.
   * @param previousFact The fact currently shown, to avoid repeating.
   */
  private pickRandomFact(facts: string[], previousFact: string): string {
    if (facts.length === 0) {
      return '';
    }
    if (facts.length === 1) {
      return facts[0];
    }
    let nextFact: string;
    do {
      nextFact = facts[Math.floor(Math.random() * facts.length)];
    } while (nextFact === previousFact);
    return nextFact;
  }

  /**
   * Handles changes to component inputs and updates transcript ID and current document
   * @param changes - Object containing the changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metadata'] && this.metadata) {
      this.transcriptId = this.metadata.transId;
      this.currentDocument.set(this.metadata);
      this.loadData().catch(error => {
        console.error('Failed to load transcript data in ngOnChanges:', error);
        this.setDataLoadError(error);
      });
    }
  }

  /**
   * Cleans up resources and unlocks transcript if locked by current user
   */
  ngOnDestroy(): void {
    clearTimeout(this.autosaveTimeout);

    if (this.hasUnsavedChanges() && this.isLockedByCurrentUser() && this.transcriptData) {
      this.saveChanges().catch(error => console.error('Failed to save changes on destroy:', error));
    }

    if (this.transcriptId && this.shouldUnlockOnDestroy && this.isLockedByCurrentUser()) {
      this.transcriptMetadataService
        .unlockTranscript(this.transcriptId)
        .catch(error => console.error('Failed to unlock transcript on destroy:', error));
    }

    this.transcriptRealtimeService.disconnect();
  }

  /**
   * Opens the confirmation dialog for changing the transcript's type.
   * @param type The transcript type selected from the dropdown
   */
  onSelectTranscriptType(type: TranscriptType): void {
    if (type === this.transcriptType()) return;
    this.newTranscriptType.set(type);
    this.showChangeTypeConfirmation.set(true);
  }

  /**
   * Handles the change-type confirmation dialog result, submitting the type
   * change and re-extraction request when confirmed.
   * @param confirm - Whether the user confirmed the type change
   */
  async handleChangeTypeConfirmation(confirm: boolean): Promise<void> {
    this.showChangeTypeConfirmation.set(false);
    const type = this.newTranscriptType();
    this.newTranscriptType.set(null);
    if (!confirm || type === null) return;

    this.isExtracting.set(true);
    try {
      await this.transcriptMetadataService.changeTranscriptType(this.transcriptId, type);
    } catch (error) {
      console.error('Failed to change transcript type:', error);
      this.isExtracting.set(false);
      this.toastService.error('transcriptErrors.error', 'transcriptErrors.changeTypeFailed');
    }
  }

  /**
   * Loads transcript data and required fields using the service
   */
  private async loadData(): Promise<void> {
    if (!this.transcriptId) {
      this.toastService.error('Error', 'No transcript ID available');
      return;
    }

    this.dataLoadError.set(false);
    this.dataLoadErrorReason.set('unknown');

    try {
      const [requiredFieldsResult, transcriptResult] = await Promise.allSettled([
        this.itpExtractionDataService.loadRequiredFields(this.transcriptType()),
        this.itpExtractionDataService.loadTranscriptData(this.transcriptId)
      ]);

      if (requiredFieldsResult.status === 'fulfilled') {
        this.requiredFields = requiredFieldsResult.value;
      } else {
        console.error('Failed to load required fields:', requiredFieldsResult.reason);
      }

      if (transcriptResult.status === 'fulfilled') {
        this.transcriptData = transcriptResult.value.data;
        this.transcriptData?.Courses.forEach(course => {
          course.id ??= crypto.randomUUID();
        });
        this.transcriptStatusId.set(transcriptResult.value.statusId);
        this.statusChanged.emit(this.getStatusText(transcriptResult.value.statusId));
        this.autoShowColumns();
        this.requiredFieldsVersion.update(v => v + 1);
      } else {
        throw transcriptResult.reason;
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      this.setDataLoadError(error);
      this.statusChanged.emit(this.getStatusText(null));
    }
  }

  /**
   * Locks the transcript for editing by the current user
   */
  async lockTranscriptForEditing(): Promise<void> {
    if (!this.transcriptId) return;

    try {
      const lockSuccess = await this.transcriptMetadataService.lockTranscript(this.transcriptId);
      if (lockSuccess) {
        const currentUser = this.fronteggAuthService.user;
        const displayName =
          (currentUser as any)?.customClaims?.display_name || currentUser?.name || '';
        this.currentDocument.update(doc => (doc ? { ...doc, lockedByUser: displayName } : doc));
        this.lockStateChanged.emit({ transcriptId: this.transcriptId, lockedByUser: displayName });
      } else {
        console.warn(
          'Failed to lock transcript - already locked by another user:',
          this.transcriptId
        );
        this.toastService.error('transcriptErrors.error', 'transcriptErrors.lockedByAnotherUser');
      }
    } catch (error) {
      console.error('Failed to lock transcript:', error);
      this.toastService.error('transcriptErrors.error', 'transcriptErrors.lockFailed');
    }
  }

  /**
   * Unlocks the transcript to allow other users to edit
   */
  async unlockTranscript(): Promise<void> {
    if (!this.transcriptId) return;

    if (this.hasUnsavedChanges()) {
      clearTimeout(this.autosaveTimeout);
      await this.saveChanges();
    }

    if (this.saveFailed()) {
      return;
    }

    try {
      await this.transcriptMetadataService.unlockTranscript(this.transcriptId);
      this.currentDocument.update(doc => (doc ? { ...doc, lockedByUser: null } : doc));
      this.lockStateChanged.emit({ transcriptId: this.transcriptId, lockedByUser: null });
    } catch (error) {
      console.error('Failed to unlock transcript:', error);
      this.toastService.error('transcriptErrors.error', 'transcriptErrors.unlockFailed');
    }
  }

  /**
   * Automatically shows columns that are required or have data when transcript data is loaded
   */
  private autoShowColumns(): void {
    const courses = this.transcriptData?.Courses || [];
    const fieldsToShow = this.itpExtractionDataService.getAutoVisibleColumns(
      courses,
      this.requiredFields.course,
      this.courseColumns()
    );

    if (fieldsToShow.length === 0) return;

    fieldsToShow.forEach(field => this.itpExtractionDataService.ensureCourseField(courses, field));

    this.courseColumns.update(cols =>
      cols.map(col => (fieldsToShow.includes(col.field) ? { ...col, visible: true } : col))
    );
  }

  /**
   * Handles field changes and triggers autosave after 2 seconds
   */
  onFieldChange(): void {
    this.hasUnsavedChanges.set(true);
    this.hasBeenEdited.set(true);
    clearTimeout(this.autosaveTimeout);
    this.requiredFieldsVersion.update(v => v + 1);
    this.autosaveTimeout = setTimeout(() => {
      this.saveChanges();
    }, 2000);
  }

  /**
   * Gets the sort order for a specific field
   * @param field - The field name to check sort order for
   * @returns The sort order (1 for ascending, -1 for descending) or null if not sorted
   */
  getSortOrder(field: string): number | null {
    return this.courseTable?.sortField === field + '.value' ? this.courseTable?.sortOrder : null;
  }

  /**
   * Toggles the visibility of a course column by its field name.
   * @param field The field name of the column to toggle visibility for.
   */
  toggleColumn(field: string): void {
    const courses = this.transcriptData?.Courses || [];
    this.itpExtractionDataService.ensureCourseField(courses, field);

    this.courseColumns.update(cols => {
      const col = cols.find(c => c.field === field);
      if (col) {
        col.visible = !col.visible;
      }
      return [...cols];
    });
  }

  /**
   * Shows bounding box for a given field on the document viewer using json bbox data
   * @param field The field object containing bounding box information to display.
   */
  async showBoundingBox(field: FieldWithBbox): Promise<void> {
    if (!this.documentViewer) return;
    this.documentViewer.clearHighlightBoxes();
    if (!field?.bbox || !field?.page) return;

    try {
      await this.documentViewer.navigateToPage(field.page);
      const [x, y, width, height] = field.bbox;
      const vertices = [
        { x: x, y: y },
        { x: x + width, y: y },
        { x: x + width, y: y + height },
        { x: x, y: y + height }
      ];

      this.documentViewer.drawBoundingBox(vertices, field.page, '#C82C32');
    } catch (error) {
      console.error('Failed to show bounding box:', error);
    }
  }

  /**
   * Highlights the bounding box for the given course row in the document viewer.
   * @param course The course row for which to show the bounding box.
   */
  showCourseRowBoundingBox(course: Course): void {
    if (!this.documentViewer) return;

    try {
      this.documentViewer.clearHighlightBoxes();

      const allFields = Object.entries(course)
        .filter(
          ([key, field]) =>
            key !== 'Term' &&
            key !== 'Institution' &&
            key !== 'StartDate' &&
            key !== 'EndDate' &&
            field?.bbox &&
            field?.page &&
            field.bbox[2] > 0 &&
            field.bbox[3] > 0
        )
        .map(([, field]) => field);

      if (allFields.length === 0) return;

      const tolerance = 0.02;
      const rowGroups: FieldWithBbox[][] = [];

      const sortedFields = allFields.sort((a, b) => a.bbox![1] - b.bbox![1]);

      sortedFields.forEach(field => {
        const fieldY = field.bbox![1];

        const lastGroup = rowGroups[rowGroups.length - 1];
        if (lastGroup && Math.abs(fieldY - lastGroup[0].bbox![1]) < tolerance) {
          lastGroup.push(field);
        } else {
          rowGroups.push([field]);
        }
      });

      const bestGroup = rowGroups.reduce((a, b) => (a.length > b.length ? a : b));

      const page = bestGroup[0].page!;
      const minX = Math.min(...bestGroup.map(f => f.bbox![0]));
      const minY = Math.min(...bestGroup.map(f => f.bbox![1]));
      const maxX = Math.max(...bestGroup.map(f => f.bbox![0] + f.bbox![2]));
      const maxY = Math.max(...bestGroup.map(f => f.bbox![1] + f.bbox![3]));

      const vertices = [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY }
      ];

      this.documentViewer
        .navigateToPage(page)
        .then(() => {
          this.documentViewer!.clearHighlightBoxes();
          this.documentViewer!.drawBoundingBox(vertices, page, '#C82C32');
        })
        .catch(error => {
          console.error('Failed to show course row bounding box:', error);
        });
    } catch (error) {
      console.error('Failed to show course row bounding box:', error);
    }
  }

  /**
   * Adds a new row above or below the selected course
   * @param position Specifies whether to add the row 'above' or 'below' the selected course
   */
  addRow(position: 'above' | 'below') {
    try {
      if (!this.transcriptData?.Courses) {
        this.toastService.error('Error', 'No transcript data available');
        return;
      }

      const selections =
        this.selectedCourses.length > 0
          ? this.selectedCourses
          : this.selectedCourse
            ? [this.selectedCourse]
            : [];
      if (selections.length === 0) {
        this.toastService.error('Error', 'No course selected');
        return;
      }

      const indices = selections
        .map(course => this.transcriptData!.Courses.indexOf(course))
        .filter(courseIndex => courseIndex !== -1);
      if (indices.length === 0) {
        this.toastService.error('Error', 'Selected course not found');
        return;
      }

      const index = position === 'above' ? Math.min(...indices) : Math.max(...indices);

      const newRow: Course = {
        id: crypto.randomUUID(),
        Term: { value: '', bbox: null, confidence: null, page: null },
        StartDate: { value: '', bbox: null, confidence: null, page: null },
        EndDate: { value: '', bbox: null, confidence: null, page: null },
        Subject: { value: '', bbox: null, confidence: null, page: null },
        CourseNumber: { value: '', bbox: null, confidence: null, page: null },
        CourseTitle: { value: '', bbox: null, confidence: null, page: null },
        CreditsEarned: { value: '', bbox: null, confidence: null, page: null },
        Grade: { value: '', bbox: null, confidence: null, page: null },
        GradePoints: { value: '', bbox: null, confidence: null, page: null },
        Institution: { value: '', bbox: null, confidence: null, page: null },
        InstitutionId: { value: '', bbox: null, confidence: null, page: null }
      };

      const insertIndex = position === 'above' ? index : index + 1;
      this.transcriptData.Courses.splice(insertIndex, 0, newRow);
      this.transcriptData.Courses = [...this.transcriptData.Courses];
      this.onFieldChange();
    } catch (error) {
      console.error('Failed to add row:', error);
      this.toastService.error('Error', 'Failed to add new row');
    }
  }

  /**
   * Deletes the selected rows from the courses list
   */
  get coursesSelected(): Course[] {
    if (this.selectedCourses.length > 0) {
      return this.selectedCourses;
    }
    return this.selectedCourse ? [this.selectedCourse] : [];
  }

  /**
   * Opens the confirmation dialog for deleting the pending course row(s)
   */
  confirmDeleteRows(): void {
    if (this.coursesSelected.length === 0) {
      this.toastService.error('Error', 'No course selected');
      return;
    }
    this.showDeleteCourseRowsConfirmation.set(true);
  }

  /**
   * Handles the delete-rows confirmation dialog result
   * @param confirm - Whether the user confirmed the deletion
   */
  handleDeleteRowsConfirmation(confirm: boolean): void {
    this.showDeleteCourseRowsConfirmation.set(false);
    if (confirm) {
      this.deleteRows(this.coursesSelected);
    }
  }

  /**
   * Deletes the given course rows from the courses list
   * @param coursesToRemove The course rows to remove
   */
  private deleteRows(coursesToRemove: Course[]): void {
    try {
      if (!this.transcriptData?.Courses) {
        this.toastService.error('Error', 'No transcript data available');
        return;
      }

      if (coursesToRemove.length === 0) {
        this.toastService.error('Error', 'No course selected');
        return;
      }

      const idsToRemove = new Set(coursesToRemove.map(course => course.id));
      const remaining = this.transcriptData.Courses.filter(course => !idsToRemove.has(course.id));

      if (remaining.length === this.transcriptData.Courses.length) {
        this.toastService.error('Error', 'Selected course not found');
        return;
      }

      this.transcriptData.Courses = remaining;
      this.selectedCourse = null;
      this.selectedCourses = [];
      this.onFieldChange();
      this.toastService.success('success', 'transcriptData.deleteCourseRowsSuccess');
    } catch (error) {
      console.error('Failed to delete rows:', error);
      this.toastService.error('transcriptErrors.error', 'transcriptErrors.deleteCourseRowsFailed');
    }
  }

  /**
   * Handles the focus event on an input and sets the selected input value.
   * @param value The value of the input that received focus.
   * @param fieldName The name of the field that received focus.
   * @param field The field object associated with the input that received focus, containing bounding box information.
   */
  onInputFocus(value: string | number | null, fieldName?: string, field?: FieldWithBbox): void {
    this.selectedInputValue.set(String(value || ''));
    this.selectedField.set(field ?? null);
  }

  /**
   * Handles the clear event on a dropdown
   *  @param field The field object associated with the input that received focus, containing bounding box information.
   */
  onClear(field: FieldWithBbox): void {
    field.value = '';
    this.onFieldChange();
  }

  /**
   * Downloads the transcript data as a JSON file
   */
  downloadTranscriptData(): void {
    if (!this.transcriptData) {
      this.toastService.error('Error', 'No transcript data available to download');
      return;
    }
    try {
      this.transcriptMetadataService.transcriptDownloadAudit(this.transcriptId);
      this.itpExtractionDataService.downloadAsJson(this.transcriptData);
    } catch (error) {
      this.toastService.error('Error', 'Failed to download transcript data');
    }
  }

  /**
   * Saves transcript changes to the metadata service
   */
  private async saveChanges(): Promise<void> {
    if (!this.hasUnsavedChanges() || !this.transcriptData) return;

    this.isSaving.set(true);
    this.saveFailed.set(false);
    try {
      const currentDoc = await this.transcriptMetadataService.getTranscriptMetadata(
        this.transcriptId
      );

      if (!currentDoc?.lockedByUserName && !currentDoc?.lockedByUser) {
        this.saveFailed.set(true);
        this.toastService.error('transcriptErrors.error', 'transcriptErrors.adminUnlockError');
        this.hasUnsavedChanges.set(false);
        return;
      }

      await this.itpExtractionDataService.saveTranscriptData(
        this.transcriptId,
        this.transcriptData
      );
      this.hasUnsavedChanges.set(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
      this.saveFailed.set(true);
      this.toastService.error('transcriptErrors.error', 'transcriptErrors.saveFailed');
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Handles the completion confirmation dialog result
   * @param confirm - Whether the user confirmed completion
   */
  async handleCompletion(confirm: boolean): Promise<void> {
    this.showCompletionConfirmation.set(false);
    if (confirm) {
      if (this.transcriptId) {
        try {
          await this.transcriptMetadataService.unlockTranscript(this.transcriptId);
          await this.transcriptMetadataService.saveStatus(
            this.transcriptId,
            TranscriptStatus.Archived
          );
          this.transcriptRealtimeService.updateTranscript(this.transcriptId, {
            extractionStatus: 'Archived'
          });
          this.router.navigate(['transcripts']);
        } catch (error) {
          console.error('Failed to complete transcript:', error);
          this.toastService.error('Error', 'Failed to complete transcript');
        }
      }
    } else {
      this.shouldUnlockOnDestroy = true;
    }
  }

  /**
   * Handles click on "Send to SIS" button, validates required fields, and shows confirmation or missing fields dialog
   */
  onClickSendToSis(): void {
    this.showValidationErrors.set(true);

    if (
      this.transcriptType() === TranscriptType.College &&
      (!this.transcriptData?.Courses || this.transcriptData.Courses.length === 0)
    ) {
      this.missingFieldsSections.set(['Courses']);
      this.showMissingFieldsDialog.set(true);
      return;
    }

    const result = this.itpExtractionValidationService.validateRequiredFields(
      this.transcriptData,
      this.requiredFields,
      this.transcriptType()
    );

    if (!result.isValid) {
      this.missingFieldsSections.set(result.missingSections);
      this.showMissingFieldsDialog.set(true);
      return;
    }

    this.showSendToSISConfirmation.set(true);
  }

  /**
   * Closes the missing fields dialog
   */
  closeMissingFieldsDialog(): void {
    this.showMissingFieldsDialog.set(false);
  }

  /**
   * Sends transcript data to SIS via the transcriptExportService
   * @param softdocsTranscriptId The Softdocs transcript ID
   * @param softdocsTranscriptUrl The Softdocs transcript URL
   */
  async sendToSis(softdocsTranscriptId: string, softdocsTranscriptUrl: string): Promise<void> {
    if (!this.transcriptData || this.isExporting()) return;

    const transcript: TranscriptToExport = {
      data: this.transcriptData,
      transcriptId: this.transcriptId,
      transcriptUrl: softdocsTranscriptUrl,
      transcriptType: this.transcriptType()
    };

    this.isExporting.set(true);
    this.showSendToSISConfirmation.set(false);
    try {
      const responses = await this.transcriptExportService.sendToSis([transcript], false);
      let response: BatchSisResponse = {} as BatchSisResponse;
      if (responses.length) {
        response = responses[0];
      }

      if (response.success) {
        this.transcriptStatusId.set(TranscriptStatus.Exported);
      } else if (response.code === SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE) {
        this.transcriptStatusId.set(TranscriptStatus.PartiallyExported);
      } else {
        const isMatchingError =
          response.code === SisErrorCodes.STUDENT_NOT_FOUND ||
          response.code === SisErrorCodes.INSTITUTION_NOT_FOUND ||
          response.code === SisErrorCodes.MULTIPLE_STUDENTS_MATCHED ||
          response.code === SisErrorCodes.MULTIPLE_INSTITUTIONS_MATCHED;
        this.transcriptStatusId.set(
          isMatchingError ? TranscriptStatus.FailedMatching : TranscriptStatus.FailedToExport
        );
      }
    } catch (error) {
      console.error('Failed to send to SIS:', error);
      this.transcriptStatusId.set(TranscriptStatus.FailedToExport);
      this.toastService.error('Error', 'Failed to send transcript to SIS');
    } finally {
      this.isExporting.set(false);
    }
  }

  /**
   * Handles cancellation of sending transcript to SIS
   */
  async handleCancelSendToSis(): Promise<void> {
    this.showSendToSISConfirmation.set(false);
  }

  /**
   * Returns the display text for a given transcript status ID, defaulting to 'Processing' if the status ID is not recognized.
   * @param statusId The transcript status ID to get the text for
   */
  getStatusText(statusId: number | null): string {
    return (this.statusConfig.get(statusId ?? -1) ?? this.defaultStatus).text;
  }

  /**
   * Initiates the transcript completion process and shows confirmation dialog
   */
  async onComplete(): Promise<void> {
    if (this.hasUnsavedChanges()) {
      clearTimeout(this.autosaveTimeout);
      await this.saveChanges();
    }

    if (this.saveFailed()) {
      return;
    }

    this.shouldUnlockOnDestroy = false;
    this.showCompletionConfirmation.set(true);
  }

  /**
   * Closes the component and navigates back to the transcripts list
   */
  async onClose(): Promise<void> {
    if (this.hasUnsavedChanges()) {
      clearTimeout(this.autosaveTimeout);
      await this.saveChanges();
    }
    this.router.navigate(['transcripts']);
  }

  /**
   * Determines whether to show a required field indicator element.
   * @param fieldName - The name of the field to check
   * @param type - The type of validation to perform
   * @param fieldValue - The value of the field to check
   * @returns Whether the required indicator should be displayed
   */
  showRequiredElement(fieldName: string, type: ValidateElementType, fieldValue: any): boolean {
    return this.itpExtractionValidationService.showRequiredElement(
      fieldName,
      type,
      fieldValue,
      this.transcriptData,
      this.requiredFields
    );
  }

  /**
   * Handles changes to the review completed checkbox
   * @param checked - The new state of the checkbox
   */
  async onReviewCompletedChange(checked: boolean): Promise<void> {
    try {
      const newStatus = checked ? TranscriptStatus.ReadyForExport : TranscriptStatus.NeedsReview;
      this.transcriptStatusId.set(newStatus);

      await this.transcriptMetadataService.saveStatus(this.transcriptId, newStatus);

      this.statusChanged.emit(this.getStatusText(newStatus));
    } catch (error) {
      console.error('Failed to update review status:', error);
      this.toastService.error('Error', 'Failed to update review status');
      this.transcriptStatusId.set(
        checked ? TranscriptStatus.NeedsReview : TranscriptStatus.ReadyForExport
      );
    }
  }

  /**
   * Gets the translation key for a section name used in the missing fields dialog
   * @param section - The section name (e.g., 'Student', 'Institution', 'Courses')
   * @returns The translation key string
   */
  getSectionKey(section: string): string {
    return `transcriptData.${section.toLowerCase()}`;
  }

  /**
   * Checks if a field is required and empty value (warning state)
   * @param fieldName - The name of the field to check
   * @param field - The field object with confidence property
   * @returns Whether the field should show warning styling
   */
  fieldsNeedingReview(fieldName: string, field: FieldWithBbox | undefined): boolean {
    if (!field || !this.isEditable() || !this.reviewIndicatorFlag()) return false;
    if (field.reviewed) return false;

    const isEmpty = !field.value || field.value === '';
    const isRequired = this.showRequiredElement(fieldName, 1, '');
    const hasLowConfidence =
      field.confidence !== null && field.confidence < REVIEW_CONFIDENCE_THRESHOLD;

    return hasLowConfidence || (isRequired && isEmpty);
  }

  /**
   * Checks if a field should show error styling (after export attempt with empty value)
   * @param fieldName - The name of the field to check
   * @param fieldValue - The current value of the field
   * @returns Whether the field should show error styling
   */
  shouldShowFieldError(fieldName: string, fieldValue: any): boolean {
    if (!this.showValidationErrors() || !this.isEditable()) return false;
    return this.showRequiredElement(fieldName, 3, fieldValue);
  }

  /**
   * Handles changes to the reviewed checkbox for a field and updates the reviewedFields set
   * @param checked - The new state of the checkbox
   */
  onReviewedChange(checked: boolean): void {
    const field = this.selectedField();
    if (!field) return;
    field.reviewed = checked;
    this.onFieldChange();
  }

  /**
   * Shows the confidence modal for the overall transcript confidence score
   */
  showOverallConfidenceModal(): void {
    if (!this.metadata?.confidenceScore) return;

    this.selectedConfidenceField = {
      value: this.metadata.confidenceScore,
      bbox: null,
      confidence: this.metadata.confidenceScore,
      page: null
    };

    this.selectedConfidenceExplanation =
      this.transcriptData?.Metadata?.ConfidenceLevelReasoning || null;
    this.confidenceModalVisible = true;
  }

  /**
   * Closes the confidence modal and resets selected confidence field and explanation
   */
  onConfidenceModalClose(): void {
    this.confidenceModalVisible = false;
    this.selectedConfidenceField = null;
    this.selectedConfidenceExplanation = null;
  }
}
